// AlphaLab links and tool labels (design-revamp.md sections 5 and 8).
import { alphalabOrigin } from "./theme.js";

/** Whether a URL is on the environment's AlphaLab app, and so may open in the panel. */
export function isAlphaLabUrl(href: string | undefined, origin: string | undefined = alphalabOrigin()): boolean {
  if (!href || !origin) return false;
  try {
    const url = new URL(href);
    return url.protocol === "https:" && url.origin === origin;
  } catch {
    return false;
  }
}

/** The panel's breakpoint: at least this wide, AlphaLab opens beside the chat. */
export const PANEL_MIN_WIDTH = 1024;

export function isWide(width: number = window.innerWidth): boolean {
  return width >= PANEL_MIN_WIDTH;
}

const LABELS: Record<string, string> = {
  list_research_projects: "Listed your research projects",
  create_research_project: "Created a research project",
  list_all_canvas: "Listed your AlphaGraphs",
  create_canvas: "Built an AlphaGraph",
  load_example_alphagraph: "Loaded an example AlphaGraph",
  get_component_contracts: "Read component contracts",
  get_all_components_meta_data: "Read the component catalog",
  search_instruments: "Searched instruments",
  get_market_data_availability: "Checked market data",
  validate_execution_request: "Validated a run",
  run_experiment: "Started a backtest",
  await_operation: "Waited for a run",
  get_run: "Read a run",
  get_run_metrics: "Read run metrics",
  list_runs: "Listed runs",
  compare_runs: "Compared runs",
  get_run_failure: "Read why a run failed",
  record_research_note: "Recorded a research note",
  list_research_notes: "Read research notes",
  inspect_research_state: "Read the research state",
  create_hypothesis: "Created a hypothesis",
  list_hypotheses: "Listed hypotheses",
  me: "Checked your account",
  list_drive_files: "Listed your Drive files",
  download_drive_file: "Read a Drive file",
  quanty_schedule: "Scheduled a check-in",
  quanty_list_schedule: "Read the schedule",
  quanty_cancel_schedule: "Cancelled a scheduled entry",
  quanty_runs_check_later: "Moved the next runs check-in",
  quanty_show_in_alphalab: "Opened AlphaLab",
};

export function toolLabel(name: string): string {
  return LABELS[name] ?? name.replace(/_/g, " ");
}

/**
 * AlphaLab's Drive page, where an attached file is (attachments.md section 8). AlphaLab has no page
 * for one file, so every file chip opens this one.
 */
export function driveUrl(origin: string | undefined = alphalabOrigin()): string | undefined {
  return origin ? `${origin}/drive` : undefined;
}

const LIBRARY_KINDS: Record<string, string> = { hypothesis: "Hypothesis", decision: "Decision", assessment: "Assessment" };

/** A run's page under /r/:runId, or under a strategy's /s/:id/r/:runId: a sub-run or a window has its own. */
function runPage([, , sub, index]: string[]): string {
  if (sub === "sr") return "Optimization sub-run";
  // The URL counts windows from 0; AlphaLab shows them from 1.
  if (sub === "w") return /^\d+$/.test(index ?? "") ? `Walk-forward window ${Number(index) + 1}` : "Walk-forward window";
  return "Run";
}

/** What an AlphaLab page is, from its path (the routes in apps/alphalab/frontend/src/App.tsx). */
export function pageKind(href: string): string {
  let path: string[];
  try {
    path = new URL(href).pathname.split("/").filter(Boolean);
  } catch {
    return "AlphaLab page";
  }
  const [first, second, third] = path;
  switch (first) {
    case undefined:
    case "home":
      return "Home";
    case "p":
      return second ? "Project" : "AlphaLab page";
    case "projects":
      return "Projects";
    case "s":
      if (third === "rev") return path[3] ? `AlphaGraph revision ${path[3]}` : "AlphaGraph";
      if (third === "r") return runPage(path.slice(2));
      if (third === "results") return "Run";
      return "AlphaGraph";
    case "r":
      return runPage(path);
    case "runs":
      return second === "compare" ? "Run comparison" : "Runs";
    case "graphs":
      return "AlphaGraphs";
    case "hypotheses":
      return "Hypothesis";
    case "library":
      return second && third ? (LIBRARY_KINDS[second] ?? "Library item") : "Library";
    case "decisions":
      return second ? "Decision" : "Decisions";
    case "confirm":
      return "Decision to confirm";
    case "artifacts":
      return "Report";
    case "work":
      if (second === "runs" && third) return "Engagement";
      if (second === "items" && third) return "Work item";
      return second ? "Desk service" : "Research Desk";
    case "strategy-agents":
      return second === "runs" && third ? "Strategy agent run" : "Strategy agents";
    case "agent-hub":
      return second === "runs" && third ? "Agent run" : "Agent hub";
    case "data":
      return second === "instruments" && third ? "Instrument" : "Data catalog";
    case "drive":
      return "Drive";
    case "settings":
      return "Settings";
    case "docs":
      return "Docs";
    default:
      return "AlphaLab page";
  }
}

/** Name fields in the order they are preferred; AlphaLab writes keys camelCase, a few PascalCase. */
const NAME_KEYS = ["[Tt]itle", "[Nn]ame", "[Ll]abel"];

/**
 * The title, name or label of the object a tool result gives this URL in (as its `url`), if any.
 * The result the app keeps is cut at 4,000 characters, so it is read as text, not parsed: the name
 * must be in the same object before the URL, as AlphaLab's records put it. Unsure means no name.
 */
export function linkName(result: string | undefined, url: string): string | undefined {
  if (!result) return undefined;
  const field = new RegExp(`"[Uu]rl"\\s*:\\s*${JSON.stringify(url).replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}`, "g");
  for (const match of result.matchAll(field)) {
    const start = result.lastIndexOf("{", match.index);
    const object = result.slice(start + 1, match.index);
    // A closed brace means the URL is not in the object that opened last.
    if (start < 0 || object.includes("}")) continue;
    for (const key of NAME_KEYS) {
      const value = new RegExp(`"${key}"\\s*:\\s*"((?:[^"\\\\]|\\\\.)*)"`).exec(object)?.[1];
      if (value === undefined) continue;
      try {
        const name = (JSON.parse(`"${value}"`) as string).trim();
        if (name) return name;
      } catch {
        // Not a JSON string after all.
      }
    }
  }
  return undefined;
}

/** A tool card link's label: what the page is, and its name when the result gives one. */
export function linkLabel(url: string, result?: string): string {
  const kind = pageKind(url);
  const name = linkName(result, url);
  return name ? `${kind}: ${name}` : kind;
}

/** The URL and title a quanty_show_in_alphalab call asked for, from its arguments. */
export function shownUrl(args: string | undefined): { url: string; title?: string } | undefined {
  if (!args) return undefined;
  try {
    const parsed = JSON.parse(args) as { url?: unknown; title?: unknown };
    if (typeof parsed.url !== "string") return undefined;
    return { url: parsed.url, ...(typeof parsed.title === "string" ? { title: parsed.title } : {}) };
  } catch {
    return undefined;
  }
}
